import {
  canonicalizeLocale,
  canonicalizeUzbekLocale,
  UZBEK_CYRILLIC_LOCALE,
  UZBEK_LATIN_LOCALE,
} from 'src/core/locale/normalize'
import { readRedirectLocale } from 'src/core/locale/url'

export {
  apiLanguageToLocale,
  canonicalizeLocale,
  getAvailableBrowserLocale,
  getPrimaryBrowserLanguage,
  localeToApiLanguage,
  normalizeLocale,
  UZBEK_CYRILLIC_LOCALE,
  UZBEK_LATIN_LOCALE,
} from 'src/core/locale/normalize'
export { getLocaleFromPath } from 'src/core/locale/url'

const DEFAULT_PROFILE_LOCALES = ['ru', 'en', 'kk', 'az', 'uk', UZBEK_LATIN_LOCALE, 'ky', 'tr', 'hi', 'bn', 'es']
const LOCALE_MAP_KEYS = new Set([...DEFAULT_PROFILE_LOCALES, UZBEK_CYRILLIC_LOCALE])

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function getConfiguredLocales() {
  return String(process.env.LOCALES || '')
    .split('|')
    .filter(Boolean)
}

export const UZBEK_CYRILLIC_ENABLED = getConfiguredLocales().includes(UZBEK_CYRILLIC_LOCALE)

function isPresent(value) {
  return value !== undefined && value !== null && value !== ''
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object') {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function findLocaleValue(value, locale) {
  const canonicalLocale = canonicalizeLocale(locale)
  if (!canonicalLocale || !isPlainObject(value)) {
    return undefined
  }

  if (hasOwn(value, canonicalLocale) && isPresent(value[canonicalLocale])) {
    return value[canonicalLocale]
  }

  for (const key of Object.keys(value)) {
    if (key !== canonicalLocale && canonicalizeLocale(key) === canonicalLocale && isPresent(value[key])) {
      return value[key]
    }
  }

  return undefined
}

function getLocalizedFieldDescriptor(key) {
  if (typeof key !== 'string') {
    return null
  }

  for (let index = 0; index < key.length; index++) {
    const separator = key[index]
    if (separator !== '_' && separator !== '-') {
      continue
    }

    const base = key.slice(0, index)
    const suffix = key.slice(index + 1)
    if (!base || !suffix) {
      continue
    }

    const locale = canonicalizeUzbekLocale(suffix)
    if (locale) {
      return { base, locale, canonicalKey: `${base}_${locale}` }
    }

    const normalizedSuffix = suffix.toLowerCase()
    if (normalizedSuffix === 'default' || normalizedSuffix === 'ru') {
      return { base, locale: normalizedSuffix, canonicalKey: `${base}_${normalizedSuffix}` }
    }
  }

  return null
}

function isLikelyLocaleMap(value) {
  const keys = Object.keys(value)
  if (!keys.length) {
    return false
  }

  let hasUzbekLocale = false

  for (const key of keys) {
    if (key === 'default') {
      continue
    }

    const locale = canonicalizeLocale(key)
    if (!LOCALE_MAP_KEYS.has(locale)) {
      return false
    }

    if (locale === UZBEK_LATIN_LOCALE || locale === UZBEK_CYRILLIC_LOCALE) {
      hasUzbekLocale = true
    }
  }

  return hasUzbekLocale
}

export {
  getStoredLocale,
  setStoredLocale,
  setStoredLocaleFromProfile,
  syncStoredLocaleFromRedirect,
} from 'src/core/locale/runtime'

export function getLocalizedValue(value, locale, fallbackLocales = [], defaultLocale = null) {
  if (!isPlainObject(value)) {
    return undefined
  }

  const canonicalLocale = canonicalizeLocale(locale)
  if (canonicalLocale !== UZBEK_CYRILLIC_LOCALE) {
    if (value[locale]) {
      return value[locale]
    }

    for (const fallbackLocale of fallbackLocales) {
      if (value[fallbackLocale]) {
        return value[fallbackLocale]
      }
    }

    return value[defaultLocale] || value?.ru
  }

  const localeCandidates = []
  const addLocaleCandidate = (candidate) => {
    const candidateLocale = canonicalizeLocale(candidate)
    if (candidateLocale && !localeCandidates.includes(candidateLocale)) {
      localeCandidates.push(candidateLocale)
    }
  }

  addLocaleCandidate(canonicalLocale)
  addLocaleCandidate(UZBEK_LATIN_LOCALE)

  for (const fallbackLocale of fallbackLocales) {
    addLocaleCandidate(fallbackLocale)
  }

  for (const localeCandidate of localeCandidates) {
    const localizedValue = findLocaleValue(value, localeCandidate)
    if (isPresent(localizedValue)) {
      return localizedValue
    }
  }

  if (hasOwn(value, 'default') && isPresent(value.default)) {
    return value.default
  }

  const defaultValue = findLocaleValue(value, defaultLocale)
  if (isPresent(defaultValue)) {
    return defaultValue
  }

  return findLocaleValue(value, 'ru')
}

export function normalizeLocalizedData(value) {
  if (!UZBEK_CYRILLIC_ENABLED) {
    return value
  }

  const seen = new WeakMap()

  function normalizeNode(node) {
    if (typeof node === 'string') {
      const trimmedNode = node.trim()
      const looksLikeJson =
        (trimmedNode.startsWith('{') && trimmedNode.endsWith('}')) ||
        (trimmedNode.startsWith('[') && trimmedNode.endsWith(']'))

      if (!looksLikeJson) {
        return node
      }

      try {
        const parsedNode = JSON.parse(node)
        if (!Array.isArray(parsedNode) && !isPlainObject(parsedNode)) {
          return node
        }

        const normalizedNode = normalizeNode(parsedNode)
        return normalizedNode === parsedNode ? node : JSON.stringify(normalizedNode)
      } catch {
        return node
      }
    }

    if (Array.isArray(node)) {
      if (seen.has(node)) {
        return seen.get(node)
      }

      const result = []
      seen.set(node, result)
      let changed = false

      for (let index = 0; index < node.length; index++) {
        const normalizedItem = normalizeNode(node[index])
        result[index] = normalizedItem
        changed ||= normalizedItem !== node[index]
      }

      if (!changed) {
        seen.set(node, node)
        return node
      }

      return result
    }

    if (!isPlainObject(node)) {
      return node
    }

    if (seen.has(node)) {
      return seen.get(node)
    }

    const result = {}
    seen.set(node, result)
    let changed = false

    for (const [key, item] of Object.entries(node)) {
      const normalizedItem = normalizeNode(item)
      result[key] = normalizedItem
      changed ||= normalizedItem !== item
    }

    const likelyLocaleMap = isLikelyLocaleMap(result)

    for (const key of Object.keys(result)) {
      const directLocale = canonicalizeUzbekLocale(key)
      const shouldAddDirectAlias = directLocale === UZBEK_CYRILLIC_LOCALE || likelyLocaleMap
      if (shouldAddDirectAlias && key !== directLocale && !hasOwn(result, directLocale)) {
        result[directLocale] = result[key]
        changed = true
      }

      const fieldDescriptor = getLocalizedFieldDescriptor(key)
      if (
        fieldDescriptor?.locale &&
        fieldDescriptor.canonicalKey !== key &&
        !hasOwn(result, fieldDescriptor.canonicalKey)
      ) {
        result[fieldDescriptor.canonicalKey] = result[key]
        changed = true
      }
    }

    const localizedFieldBases = new Set()
    for (const key of Object.keys(result)) {
      const fieldDescriptor = getLocalizedFieldDescriptor(key)
      if (fieldDescriptor) {
        localizedFieldBases.add(fieldDescriptor.base)
      }
    }

    for (const base of localizedFieldBases) {
      const cyrillicKey = `${base}_${UZBEK_CYRILLIC_LOCALE}`
      if (hasOwn(result, cyrillicKey) && isPresent(result[cyrillicKey])) {
        continue
      }

      const fallbackKeys = [`${base}_${UZBEK_LATIN_LOCALE}`, `${base}_default`, base, `${base}_ru`]
      const fallbackKey = fallbackKeys.find((key) => hasOwn(result, key) && isPresent(result[key]))

      if (fallbackKey) {
        result[cyrillicKey] = result[fallbackKey]
        changed = true
      }
    }

    if (likelyLocaleMap && !isPresent(result[UZBEK_CYRILLIC_LOCALE])) {
      const fallbackValue = getLocalizedValue(result, UZBEK_CYRILLIC_LOCALE, [UZBEK_LATIN_LOCALE])
      if (isPresent(fallbackValue)) {
        result[UZBEK_CYRILLIC_LOCALE] = fallbackValue
        changed = true
      }
    }

    if (!changed) {
      seen.set(node, node)
      return node
    }

    return result
  }

  return normalizeNode(value)
}

export function getLocaleFromRedirectSearch(search, baseUrl, availableLocales) {
  return readRedirectLocale(search, baseUrl, availableLocales, UZBEK_CYRILLIC_ENABLED)
}

export function localeForExternalService(value, options) {
  const locale = canonicalizeLocale(value)
  if (options?.platform === 'alt-w') {
    return locale
  }
  return locale === UZBEK_CYRILLIC_LOCALE ? UZBEK_LATIN_LOCALE : locale
}

export function getLocalizedField(source, field, value) {
  if (!source || !field) {
    return null
  }

  const locale = canonicalizeLocale(value)
  const localeCandidates = locale === UZBEK_CYRILLIC_LOCALE ? [locale, UZBEK_LATIN_LOCALE] : [locale]

  for (const localeCandidate of localeCandidates) {
    const localizedValue = localeCandidate ? source[`${field}_${localeCandidate}`] : null

    if (localizedValue) {
      return localizedValue
    }
  }

  return source[field] || null
}
